import { create } from 'zustand';
import { useEffect, useMemo } from 'react';
import { parseISO, eachDayOfInterval, format } from 'date-fns';
import * as R from 'ramda';
import useMediaValueReports from './useMediaValueReports';
import getFilters from '../../selectors/getSelectedFilters';
import { MediaValueReport } from '@app/types/api';

enum TopSponsorsGroupBy {
    exposure = 'exposure',
    smv = 'smv',
}

interface TopSponsorData {
    id: number;
    name: string;
    exposures: number;
    smv: number;
    logoUrl: string;
}

enum Granularity {
    day = 'day',
    week = 'week',
    month = 'month',
}

interface ChartDataPoint {
    id: number;
    name: string;
    smv: number;
    exposures: number;
    date: string;
}

type State = {
    selectedSponsorIds: Set<number>;
    topSponsorsData: TopSponsorData[];
    groupBy: TopSponsorsGroupBy;
    isLoadingMediaValueReportTopSponsors: boolean;
    isLoadingMediaValueReportGraph: boolean;
    chartDataPoints: ChartDataPoint[];
};

type Actions = {
    setSelectedSponsors: (newSelectedSponsorIds: Set<number>) => void;
    toggleSelectedSponsor: (sponsorId: number) => void;
    setTopSponsorsData: (newTopSponsorsData: TopSponsorData[]) => void;
    setGroupBy: (newGroupBy: TopSponsorsGroupBy) => void;
    setIsLoadingMediaValueReportTopSponsors: (isLoading: boolean) => void;
    setIsLoadingMediaValueReportGraph: (isLoading: boolean) => void;
    setChartDataPoints: (newChartDataPoints: ChartDataPoint[]) => void;
};

const useTopSponsorsCardStore = create<State & Actions>((set, get) => ({
    selectedSponsorIds: new Set(),
    topSponsorsData: [],
    groupBy: TopSponsorsGroupBy.exposure,
    isLoadingMediaValueReportTopSponsors: false,
    isLoadingMediaValueReportGraph: false,
    chartDataPoints: [],
    setSelectedSponsors: (newSelectedSponsorIds: Set<number>) => {
        set({ selectedSponsorIds: new Set(newSelectedSponsorIds) });
    },
    toggleSelectedSponsor: (sponsorId: number) => {
        const { selectedSponsorIds } = get();
        const isUnselectingLast =
            selectedSponsorIds.has(sponsorId) && selectedSponsorIds.size === 1;
        if (isUnselectingLast) return;
        selectedSponsorIds.delete(sponsorId) ||
            selectedSponsorIds.add(sponsorId);
        set({ selectedSponsorIds: new Set(selectedSponsorIds) });
    },
    setTopSponsorsData: (newTopSponsorsData: TopSponsorData[]) => {
        set({ topSponsorsData: newTopSponsorsData });
    },
    setGroupBy: (newGroupBy: TopSponsorsGroupBy) => {
        set({ groupBy: newGroupBy });
    },
    setIsLoadingMediaValueReportTopSponsors: (isLoading: boolean) => {
        set({ isLoadingMediaValueReportTopSponsors: isLoading });
    },
    setIsLoadingMediaValueReportGraph: (isLoading: boolean) => {
        set({ isLoadingMediaValueReportGraph: isLoading });
    },
    setChartDataPoints: (newChartDataPoints: ChartDataPoint[]) => {
        set({ chartDataPoints: newChartDataPoints });
    },
}));

const getChartDataPoints = (
    fetchedMediaValueReportGraph: MediaValueReport,
    topSponsorsData: TopSponsorData[],
    selectedSponsorIds: Set<number>,
    granularity: Granularity,
) => {
    if (fetchedMediaValueReportGraph.values.length === 0) return [];

    const datesRange = eachDayOfInterval({
        start: parseISO(fetchedMediaValueReportGraph.values[0].date),
        end: parseISO(
            fetchedMediaValueReportGraph.values[
                fetchedMediaValueReportGraph.values.length - 1
            ].date,
        ),
    });

    const dateLabels = datesRange.reduce((acc, date) => {
        const sameYear = date.getFullYear() === new Date().getFullYear();
        const idFormattedDate = format(date, 'yyyy-MM-dd');
        const formattedDate = format(date, sameYear ? 'MMM do' : 'MMM do yyyy');

        switch (granularity) {
            case Granularity.day: {
                acc.push({ dateId: idFormattedDate, label: formattedDate });
                break;
            }
            case Granularity.week: {
                const dayOfWeek = date.getDay();
                if (dayOfWeek === 1) {
                    // Monday is 1
                    const formattedDate = format(
                        date,
                        sameYear ? "'Week' MMM do" : "yyyy 'Week' MMM do",
                    );
                    acc.push({ dateId: idFormattedDate, label: formattedDate });
                }
                break;
            }
            case Granularity.month: {
                const dayOfMonth = date.getDate();
                if (dayOfMonth === 1) {
                    const formattedDate = format(
                        date,
                        sameYear ? 'MMM' : 'MMM yyyy',
                    );
                    acc.push({ dateId: idFormattedDate, label: formattedDate });
                }
                break;
            }
            default: {
                acc.push({ dateId: idFormattedDate, label: formattedDate });
                break;
            }
        }
        return acc;
    }, []);

    const dataMap = fetchedMediaValueReportGraph.values.reduce((acc, value) => {
        const itExists = !!acc[value.date];
        const sponsorId = value.sponsorId;

        if (itExists) {
            acc[value.date][sponsorId] = {
                exposures: value?.exposures ?? 0,
                smv: value?.sponsorMediaValue ?? 0,
            };
        } else {
            acc[value.date] = {
                [sponsorId]: {
                    exposures: value?.exposures ?? 0,
                    smv: value?.sponsorMediaValue ?? 0,
                },
            };
        }
        return acc;
    }, {});

    const data = topSponsorsData.reduce((acc, value) => {
        const values = dateLabels.map(({ dateId }) => {
            return R.path([dateId, value.id], dataMap) || 0;
        });

        acc[value.id] = {
            id: value.id,
            label: value.name,
            smvAndExposureValues: values,
        };

        return acc;
    }, {});

    const selectedData: ChartDataPoint[] = Array.from(
        selectedSponsorIds,
    ).reduce((acc, sponsorId) => {
        if (data[sponsorId]?.smvAndExposureValues) {
            const name = topSponsorsData.find(
                ({ id }) => id === sponsorId,
            )?.name;
            const additionalData = data[sponsorId].smvAndExposureValues.map(
                ({ exposures, smv }, index) => ({
                    id: sponsorId,
                    name,
                    smv: smv || 0,
                    exposures: exposures || 0,
                    date: dateLabels[index].label,
                }),
            );

            acc.push(...additionalData);
        }
        return acc;
    }, []);

    return selectedData;
};

const useSetUpTopSponsorsCard = (
    filters: any,
    attributionQueryFields: Record<string, boolean> = {},
) => {
    const {
        groupBy,
        topSponsorsData,
        selectedSponsorIds,
        setSelectedSponsors,
        setTopSponsorsData,
        setIsLoadingMediaValueReportTopSponsors,
        setIsLoadingMediaValueReportGraph,
        setChartDataPoints,
    } = useTopSponsorsCardStore();
    const globFilters: any = useMemo(() => getFilters(filters), [filters]);
    const granularity: Granularity = (() => {
        const daysCount = eachDayOfInterval({
            start: parseISO(globFilters.startDate),
            end: parseISO(globFilters.endDate),
        }).length;
        if (daysCount <= 7) {
            return Granularity.day;
        } else if (daysCount <= 30) {
            return Granularity.week;
        } else {
            return Granularity.month;
        }
    })();

    // Set up top sponsors by exposure or smv
    const topSponsorsQuery = useMemo(() => {
        return {
            ...globFilters,
            ...attributionQueryFields,
            groupBy: ['sponsor'],
            orderBy:
                groupBy === TopSponsorsGroupBy.exposure
                    ? ['exposures-desc']
                    : ['sponsorMediaValue-desc'],
            displayColumns: ['exposures', 'sponsorMediaValue'],
            includeTextTypeExposures: true,
            limit: 10,
        };
    }, [globFilters, groupBy, attributionQueryFields]);

    const {
        data: fetchedMediaValueReportTopSponsors,
        isLoading: isLoadingMediaValueReportTopSponsors,
    } = useMediaValueReports(topSponsorsQuery);

    useEffect(() => {
        setIsLoadingMediaValueReportTopSponsors(
            isLoadingMediaValueReportTopSponsors,
        );
        setChartDataPoints([]);
        if (
            !isLoadingMediaValueReportTopSponsors &&
            fetchedMediaValueReportTopSponsors?.values
        ) {
            // Set all sponsors as selected
            setSelectedSponsors(
                new Set(
                    fetchedMediaValueReportTopSponsors.values.map(
                        v => v.sponsorId,
                    ),
                ),
            );

            // Set top sponsors data
            setTopSponsorsData(
                fetchedMediaValueReportTopSponsors.values.map(value => {
                    // Find the logo marked as the main one
                    const sponsorLogoUrl =
                        filters?.sponsors?.list[value.sponsorId]?.logos?.find(
                            l => l.main,
                        )?.url ?? '';

                    return {
                        id: value.sponsorId,
                        name: value.sponsorName,
                        exposures: value.exposures,
                        smv: value.sponsorMediaValue,
                        logoUrl: sponsorLogoUrl,
                    };
                }),
            );
        }
    }, [
        isLoadingMediaValueReportTopSponsors,
        fetchedMediaValueReportTopSponsors,
    ]);

    const sponsorIds = useMemo(
        () => topSponsorsData.map(sp => sp.id),
        [topSponsorsData],
    );

    // Set up top sponsors by date for line graph
    const sponsorsGraphQuery = useMemo(() => {
        return {
            ...globFilters,
            ...attributionQueryFields,
            groupBy: ['sponsor', 'date'],
            orderBy: ['date-asc'],
            displayColumns: ['sponsorMediaValue', 'exposures'],
            sponsorIds: sponsorIds,
            includeTextTypeExposures: true,
            granularity: granularity,
        };
    }, [globFilters, sponsorIds, attributionQueryFields]);

    const {
        data: fetchedMediaValueReportGraph,
        isLoading: isLoadingMediaValueReportGraph,
    } = useMediaValueReports(
        sponsorsGraphQuery,
        isLoadingMediaValueReportTopSponsors || sponsorIds.length > 0,
    );

    useEffect(() => {
        setIsLoadingMediaValueReportGraph(isLoadingMediaValueReportGraph);
        if (
            !isLoadingMediaValueReportGraph &&
            fetchedMediaValueReportGraph?.values
        ) {
            const selectedData = getChartDataPoints(
                fetchedMediaValueReportGraph,
                topSponsorsData,
                selectedSponsorIds,
                granularity,
            );

            setChartDataPoints(selectedData);
        }
    }, [
        fetchedMediaValueReportGraph,
        isLoadingMediaValueReportGraph,
        topSponsorsData,
        selectedSponsorIds,
    ]);
};

export {
    TopSponsorsGroupBy,
    TopSponsorData,
    ChartDataPoint,
    useTopSponsorsCardStore,
    useSetUpTopSponsorsCard,
};
